/** Google Sheet locale for badge submissions (form timestamps, no timezone). */
const SPREADSHEET_TZ = 'Europe/London';

/** `M/D/YYYY H:mm:ss` as exported by the responses TSV. */
const SHEET_TIMESTAMP_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2}):(\d{2})$/;

const londonWallClockAsUtcMs = (date: Date): number => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: SPREADSHEET_TZ,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const n = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value);
  let hour = n('hour');
  if (hour === 24) hour = 0;
  return Date.UTC(n('year'), n('month') - 1, n('day'), hour, n('minute'), n('second'));
};

/** Instant at which London's wall clock is `year-month-day hour:minute:second`. */
const fromLondonWallClock = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number
): Date => {
  const target = Date.UTC(year, month - 1, day, hour, minute, second);
  let utc = target;
  for (let i = 0; i < 3; i++) {
    const offset = londonWallClockAsUtcMs(new Date(utc)) - utc;
    utc = target - offset;
  }
  return new Date(utc);
};

/**
 * Parse a responses-sheet timestamp. The TSV is naive London wall-clock, not the
 * viewer's local zone — `new Date('10/19/2026 13:28:20')` in Colombia is 18:28Z
 * and sorts catalog `+01:00` stamps six hours too early.
 */
export const parseSpreadsheetTimestamp = (raw: string): Date => {
  const match = raw.trim().match(SHEET_TIMESTAMP_RE);
  if (!match) return new Date(raw);
  const [, month, day, year, hour, minute, second] = match;
  return fromLondonWallClock(Number(year), Number(month), Number(day), Number(hour), Number(minute), Number(second));
};
